import React, { useState, useEffect } from 'react';
import {
  CompetitionType,
  CompetitionCategory,
  CreateIndividualCompetitorInput,
  CreateTeamCompetitorInput
} from '../types/competitor';
import { Athlete } from '../types/athlete';
import { apiService } from '../services';

interface CompetitorFormProps {
  onSubmit: (competitor: CreateIndividualCompetitorInput | CreateTeamCompetitorInput, type: CompetitionType) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const CompetitorForm: React.FC<CompetitorFormProps> = ({ onSubmit, onCancel, loading = false }) => {
  const [competitionType, setCompetitionType] = useState<CompetitionType>(CompetitionType.INDIVIDUAL);
  const [category, setCategory] = useState<CompetitionCategory>(CompetitionCategory.RX);
  const [selectedAthleteId, setSelectedAthleteId] = useState<string>('');
  const [selectedAthleteIds, setSelectedAthleteIds] = useState<string[]>([]);
  const [teamName, setTeamName] = useState('');
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [loadingAthletes, setLoadingAthletes] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAthletes();
  }, []);

  const loadAthletes = async () => {
    try {
      setLoadingAthletes(true);
      const data = await apiService.get<Athlete[]>('/athletes');
      setAthletes(data);
    } catch (err: any) {
      setError('Failed to load athletes: ' + err.message);
    } finally {
      setLoadingAthletes(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (competitionType === CompetitionType.INDIVIDUAL) {
      if (!selectedAthleteId) {
        setError('Please select an athlete');
        return;
      }

      try {
        await onSubmit(
          {
            athleteId: selectedAthleteId,
            category,
          },
          CompetitionType.INDIVIDUAL
        );
      } catch (err: any) {
        setError(err.message || 'Failed to add competitor');
      }
    } else {
      if (selectedAthleteIds.length === 0) {
        setError('Please select at least one athlete');
        return;
      }
      if (!teamName.trim()) {
        setError('Please enter a team name');
        return;
      }

      try {
        await onSubmit(
          {
            athleteIds: selectedAthleteIds,
            name: teamName.trim(),
            category,
          },
          CompetitionType.TEAM
        );
      } catch (err: any) {
        setError(err.message || 'Failed to add competitor');
      }
    }
  };

  const toggleAthleteSelection = (athleteId: string) => {
    if (selectedAthleteIds.includes(athleteId)) {
      setSelectedAthleteIds(selectedAthleteIds.filter(id => id !== athleteId));
    } else {
      setSelectedAthleteIds([...selectedAthleteIds, athleteId]);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Competition Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Competition Type *
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setCompetitionType(CompetitionType.INDIVIDUAL)}
            className={`px-4 py-3 rounded-lg border-2 transition-all ${
              competitionType === CompetitionType.INDIVIDUAL
                ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold'
                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
            }`}
          >
            <svg className="h-5 w-5 mx-auto mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Individual
          </button>
          <button
            type="button"
            onClick={() => setCompetitionType(CompetitionType.TEAM)}
            className={`px-4 py-3 rounded-lg border-2 transition-all ${
              competitionType === CompetitionType.TEAM
                ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold'
                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
            }`}
          >
            <svg className="h-5 w-5 mx-auto mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Team
          </button>
        </div>
      </div>

      {/* Category */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Category *
        </label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as CompetitionCategory)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value={CompetitionCategory.RX}>RX</option>
          <option value={CompetitionCategory.SCALED}>Scaled</option>
        </select>
      </div>

      {loadingAthletes ? (
        <div className="flex justify-center py-6">
          <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      ) : athletes.length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">No athletes available. Please create athletes first.</p>
        </div>
      ) : (
        <>
          {/* Individual Athlete Selection */}
          {competitionType === CompetitionType.INDIVIDUAL && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Athlete *
              </label>
              <select
                value={selectedAthleteId}
                onChange={(e) => setSelectedAthleteId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Select an athlete --</option>
                {athletes.map((athlete) => (
                  <option key={athlete.id} value={athlete.id}>
                    {athlete.name} {athlete.lastName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Team Configuration */}
          {competitionType === CompetitionType.TEAM && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Name *
                </label>
                <input
                  type="text"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="e.g., Team Alpha, The Warriors"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Team Members * ({selectedAthleteIds.length} selected)
                </label>
                <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
                  {athletes.map((athlete) => (
                    <div
                      key={athlete.id}
                      onClick={() => toggleAthleteSelection(athlete.id)}
                      className={`px-4 py-3 border-b border-gray-200 last:border-b-0 cursor-pointer transition-colors ${
                        selectedAthleteIds.includes(athlete.id)
                          ? 'bg-blue-50 hover:bg-blue-100'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedAthleteIds.includes(athlete.id)}
                          onChange={() => {}}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-3 text-sm text-gray-900">
                          {athlete.name} {athlete.lastName}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}

      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading || loadingAthletes || athletes.length === 0}
          className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Adding...' : 'Add Competitor'}
        </button>
      </div>
    </form>
  );
};

export default CompetitorForm;
